import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Shield, Eye, EyeOff, Lock, KeyRound, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [lockoutMessage, setLockoutMessage] = useState('');

  // MFA state
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaSession, setMfaSession] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaName, setMfaName] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLockoutMessage('');
    
    if (!formData.email || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/admin/login', {
        email: formData.email,
        password: formData.password
      });
      
      if (response.data.mfa_required) {
        // Password verified — now need TOTP
        setMfaStep(true);
        setMfaSession(response.data.mfa_session);
        setMfaName(response.data.admin?.first_name || '');
        setMfaCode('');
        return;
      }

      // No MFA — login complete
      localStorage.setItem('adminToken', response.data.token);
      localStorage.setItem('adminUser', JSON.stringify(response.data.admin));
      toast.success(`Welcome back, ${response.data.admin.first_name}!`);
      navigate('/admin');
      
    } catch (error) {
      const status = error.response?.status;
      const message = error.response?.data?.detail || 'Login failed';
      if (status === 423) {
        setLockoutMessage(message);
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    if (!mfaCode.trim()) {
      toast.error('Please enter your verification code');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/admin/login/mfa', {
        mfa_session: mfaSession,
        code: mfaCode.trim(),
      });

      localStorage.setItem('adminToken', response.data.token);
      localStorage.setItem('adminUser', JSON.stringify(response.data.admin));
      toast.success(`Welcome back, ${response.data.admin.first_name}!`);
      navigate('/admin');
    } catch (error) {
      const message = error.response?.data?.detail || 'Verification failed';
      toast.error(message);
      if (message.includes('expired')) {
        setMfaStep(false);
        setMfaSession('');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy via-navy to-teal-deep flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <AnimatePresence mode="wait">
          {!mfaStep ? (
            <motion.div
              key="password-step"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl"
            >
              {/* Header */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-navy/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="text-navy" size={32} />
                </div>
                <h1 className="font-display text-2xl text-navy mb-2">Admin Login</h1>
                <p className="text-text-mid text-sm">
                  Sign in to access the admin dashboard
                </p>
              </div>

              {/* Lockout Warning */}
              {lockoutMessage && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Lock className="text-red-500 flex-shrink-0 mt-0.5" size={18} />
                    <div className="text-sm text-red-800">
                      <p className="font-semibold mb-1">Account Locked</p>
                      <p>{lockoutMessage}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="admin@example.com"
                    className="rounded-xl border-seafoam"
                    required
                    disabled={loading}
                    data-testid="admin-login-email"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link 
                      to="/admin/forgot-password" 
                      className="text-sm text-teal hover:text-teal-deep"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      placeholder="Enter your password"
                      className="rounded-xl border-seafoam pr-10"
                      required
                      disabled={loading}
                      data-testid="admin-login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-light hover:text-text-mid"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-navy hover:bg-navy/90 text-white rounded-full py-6 font-semibold"
                  data-testid="admin-login-submit"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-border-subtle text-center">
                <p className="text-sm text-text-light">
                  Member login?{' '}
                  <Link to="/login" className="text-teal hover:text-teal-deep font-medium">
                    Go to member login
                  </Link>
                </p>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="mfa-step"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl"
            >
              {/* MFA Header */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-teal/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <KeyRound className="text-teal" size={32} />
                </div>
                <h1 className="font-display text-2xl text-navy mb-2">Two-Factor Authentication</h1>
                <p className="text-text-mid text-sm">
                  {mfaName ? `Welcome back, ${mfaName}. ` : ''}Enter the 6-digit code from your authenticator app.
                </p>
              </div>

              <form onSubmit={handleMfaSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="mfa-code">Verification Code</Label>
                  <Input
                    id="mfa-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={8}
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\s/g, ''))}
                    placeholder="000000"
                    className="rounded-xl border-seafoam text-center text-2xl tracking-[0.3em] font-mono"
                    autoFocus
                    disabled={loading}
                    data-testid="mfa-code-input"
                  />
                  <p className="text-xs text-text-mid text-center mt-1">
                    You can also use a backup code
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={loading || mfaCode.length < 6}
                  className="w-full bg-teal hover:bg-teal-deep text-white rounded-full py-6 font-semibold"
                  data-testid="mfa-submit-btn"
                >
                  {loading ? 'Verifying...' : 'Verify'}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-border-subtle text-center">
                <button
                  onClick={() => { setMfaStep(false); setMfaSession(''); setMfaCode(''); }}
                  className="text-sm text-text-mid hover:text-navy flex items-center gap-1 mx-auto"
                >
                  <ArrowLeft size={14} /> Back to login
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Right side - Branding */}
      <div className="hidden lg:flex flex-1 items-center justify-center p-12">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="text-center text-white"
        >
          <h2 className="font-display text-4xl mb-4">
            Cali<span className="italic text-seafoam">Moms</span> Connect
          </h2>
          <p className="text-white/70 text-lg max-w-md">
            Admin Dashboard
          </p>
          <div className="mt-8 flex items-center justify-center gap-4">
            <div className="w-2 h-2 bg-teal rounded-full"></div>
            <span className="text-white/50 text-sm">Secure Admin Access</span>
            <div className="w-2 h-2 bg-teal rounded-full"></div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
